/**
 * Composant d'impression des emplois du temps d'examens.
 * Supporte le format A3 (paysage) et A4 (portrait ou paysage).
 * En-tête institutionnel avec logo, mise en forme officielle.
 */

import React, { useState, useRef, useCallback } from 'react';
import { Printer, Eye, Loader2, FileText, FileDown } from 'lucide-react';
import { useGetTousEmploisQuery, EmploiTempsComplet } from '../../services/emploisTempsApi';
import { useGetInfoInstitutQuery } from '../../services/coreApi';
import { API_URL } from '../../services/baseApi';

interface Props {
  sessionId: number;
  isDark: boolean;
}

type FormatPapier = 'A4-paysage' | 'A3-paysage' | 'A4-portrait';

const FONT_OPTIONS = [
  { value: "'Calibri', 'Segoe UI', sans-serif", label: 'Calibri' },
  { value: "'Arial', sans-serif", label: 'Arial' },
  { value: "'Times New Roman', serif", label: 'Times New Roman' },
  { value: "'Georgia', serif", label: 'Georgia' },
  { value: "'Verdana', sans-serif", label: 'Verdana' },
  { value: "'Trebuchet MS', sans-serif", label: 'Trebuchet MS' },
  { value: "'Tahoma', sans-serif", label: 'Tahoma' },
  { value: "'Courier New', monospace", label: 'Courier New' },
];

const EmploiTempsImpression: React.FC<Props> = ({ sessionId, isDark }) => {
  const [format, setFormat] = useState<FormatPapier>('A4-paysage');
  const [showEnseignants, setShowEnseignants] = useState(false);
  const [hideInactif, setHideInactif] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [fontFamily, setFontFamily] = useState(FONT_OPTIONS[0].value);
  const [fontScale, setFontScale] = useState(100); // pourcentage (80-150)
  const printRef = useRef<HTMLDivElement>(null);

  const { data: emplois = [], isLoading } = useGetTousEmploisQuery(sessionId);
  const { data: institut } = useGetInfoInstitutQuery();

  const [exportingType, setExportingType] = useState<'pdf' | 'word' | null>(null);

  const handleExport = useCallback(async (type: 'pdf' | 'word') => {
    const toExport = selectedIndex !== null
      ? [emplois[selectedIndex]]
      : emplois;

    if (toExport.length === 0) return;
    setExportingType(type);

    const endpoint = type === 'pdf' ? 'export_pdf' : 'export_word';
    const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    const annee = localStorage.getItem('annee_academique');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (annee) headers['X-Annee-Academique'] = annee;

    try {
      for (const emploi of toExport) {
        if (!emploi) continue;
        const niveau = emploi.niveau || 'L1';
        const poles = emploi.poles || [];
        const pole = poles.some((p: string) => p.toLowerCase().includes('poly'))
          ? 'polytechnique'
          : 'commerce_juridique';
        const url = `${API_URL}/emplois-temps/sessions/${emploi.session_id}/${endpoint}/?niveau=${niveau}&pole=${pole}&page_format=${format}&hide_inactif=${hideInactif}&show_enseignants=${showEnseignants}`;

        const resp = await fetch(url, { headers });
        if (!resp.ok) {
          const errText = await resp.text();
          console.error(`Export ${type} failed for ${niveau}/${pole}:`, errText);
          continue;
        }

        const blob = await resp.blob();
        const ext = type === 'pdf' ? 'pdf' : 'docx';
        const filename = `emploi_temps_${niveau}_${pole}.${ext}`;
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExportingType(null);
    }
  }, [selectedIndex, emplois, format, hideInactif, showEnseignants]);

  const handlePrint = () => {
    if (!printRef.current) return;

    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const isA3 = format === 'A3-paysage';
    const isLandscape = format !== 'A4-portrait';
    const pageSize = isA3 ? 'A3' : 'A4';
    const s = (basePt: number) => `${(basePt * fontScale / 100).toFixed(1)}pt`;

    // Couleurs charte 2I
    const NAVY = '#1A2E4A';
    const NAVY_LIGHT = '#1F4788';
    const GOLD = '#FFD700';
    const GOLD_DARK = '#D4A017';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Emploi du Temps Examens - 2I</title>
        <style>
          @page {
            size: ${pageSize} ${isLandscape ? 'landscape' : 'portrait'};
            margin: 8mm 6mm;
          }

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          body {
            font-family: ${fontFamily};
            font-size: ${s(isA3 ? 11 : 9)};
            color: #1a1a1a;
            background: #fff;
          }

          .page-break { page-break-after: always; }
          .emploi-page { width: 100%; padding: 0; background: #fff; }

          /* En-tête */
          .header-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
          .header-table td { vertical-align: top; padding: 0; }

          .header-left { width: 45%; padding-right: 10px; }

          .header-left .ministere {
            font-weight: 700;
            font-size: ${s(isA3 ? 9 : 7.5)};
            text-transform: uppercase;
            line-height: 1.3;
            color: ${NAVY};
          }

          .header-left .institut-badge {
            background: ${GOLD} !important;
            font-weight: 700;
            font-size: ${s(isA3 ? 11 : 9)};
            padding: 4px 10px;
            margin: 5px 0;
            display: inline-block;
            border-radius: 3px;
            color: ${NAVY};
            border: 1px solid ${GOLD_DARK};
          }

          .header-left .contact {
            font-size: ${s(isA3 ? 8 : 7)};
            line-height: 1.5;
            color: #333;
          }

          .header-center {
            width: 30%;
            text-align: center;
            vertical-align: middle !important;
          }

          .header-center .exam-title {
            background: ${NAVY} !important;
            color: #fff !important;
            font-weight: 700;
            font-size: ${s(isA3 ? 13 : 11)};
            padding: 8px 14px;
            border-radius: 5px;
            text-transform: uppercase;
            display: inline-block;
            margin-bottom: 8px;
            letter-spacing: 1px;
            border: 2px solid ${GOLD};
          }

          .header-center .session-badge {
            background: ${GOLD} !important;
            font-weight: 700;
            font-size: ${s(isA3 ? 10 : 9)};
            padding: 3px 14px;
            display: inline-block;
            border-radius: 3px;
            color: ${NAVY} !important;
            border: 1px solid ${GOLD_DARK};
          }

          .header-right {
            width: 25%;
            text-align: center;
          }

          .header-right .republique {
            font-weight: 700;
            font-size: ${s(isA3 ? 8 : 7.5)};
            color: ${NAVY};
          }

          .header-right .devise {
            font-size: ${s(isA3 ? 7 : 6.5)};
            font-style: italic;
            margin-bottom: 6px;
            color: #555;
          }

          .header-right img {
            width: ${isA3 ? '85px' : '75px'};
            height: ${isA3 ? '85px' : '75px'};
            object-fit: contain;
          }

          /* Sous-titre niveau/pole */
          .subtitle-bar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: ${NAVY} !important;
            border: 2px solid ${GOLD};
            border-radius: 5px;
            padding: ${isA3 ? '6px 14px' : '6px 14px'};
            margin-bottom: 10px;
          }

          .subtitle-bar .niveau {
            font-weight: 700;
            font-size: ${s(isA3 ? 12 : 10)};
            color: ${GOLD} !important;
          }

          .subtitle-bar .pole {
            font-weight: 700;
            font-size: ${s(isA3 ? 13 : 11)};
            text-transform: uppercase;
            color: #fff !important;
            letter-spacing: 0.5px;
          }

          .subtitle-bar .info {
            font-size: ${s(isA3 ? 9 : 8)};
            color: #ccc !important;
          }

          /* Tableau */
          .schedule-table {
            width: 100%;
            border-collapse: collapse;
            font-size: ${s(isA3 ? 10 : 8.5)};
            border: 2px solid ${NAVY};
          }

          .schedule-table th {
            background: ${NAVY} !important;
            color: ${GOLD} !important;
            font-weight: 700;
            font-size: ${s(isA3 ? 10 : 8.5)};
            padding: ${isA3 ? '7px 8px' : '6px 8px'};
            border: 1px solid ${NAVY};
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          .schedule-table td {
            border: 1px solid #b0b0b0;
            padding: ${isA3 ? '6px 8px' : '5px 6px'};
            text-align: center;
            vertical-align: middle;
            color: ${NAVY};
          }

          /* Coloration alternée retirée : chaque jour a sa propre couleur
             (voir palette JOUR_COLORS appliquée inline sur chaque td). */

          .date-cell {
            font-weight: 700;
            background: #FFF8E1 !important;
            width: ${isA3 ? '100px' : '80px'};
            border-right: 2px solid ${GOLD_DARK} !important;
          }

          .date-cell .day-name {
            font-size: ${s(isA3 ? 9 : 7.5)};
            color: ${NAVY} !important;
            font-weight: 700;
          }

          .date-cell .day-date {
            font-size: ${s(isA3 ? 8 : 7)};
            color: #555;
          }

          .heure-cell {
            font-weight: 700;
            width: ${isA3 ? '80px' : '65px'};
            background: #EEF2F7 !important;
            color: ${NAVY} !important;
            font-size: ${s(isA3 ? 9 : 7.5)};
          }

          .matiere-cell {
            font-size: ${s(isA3 ? 9.5 : 8)};
            min-height: 30px;
            font-weight: 600;
            color: ${NAVY} !important;
          }

          .enseignant-nom {
            font-size: ${s(isA3 ? 7 : 6)};
            font-style: italic;
            color: #777 !important;
            margin-top: 2px;
          }

          /* Pied de page */
          .footer-section {
            margin-top: 14px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }

          .footer-left {
            font-size: ${s(isA3 ? 7.5 : 6.5)};
            color: #888;
            border-top: 1px solid ${GOLD};
            padding-top: 5px;
          }

          .footer-right {
            text-align: right;
            font-size: ${s(isA3 ? 9 : 7.5)};
          }

          .footer-right .lieu { margin-bottom: 3px; }
          .footer-right .service { font-style: italic; margin-bottom: 2px; color: #444; }
          .footer-right .signature { font-weight: 700; margin-top: 20px; color: ${NAVY}; }
        </style>
      </head>
      <body>
        ${printContent}
      </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  const emploisToShow = selectedIndex !== null ? [emplois[selectedIndex]] : emplois;

  return (
    <div className="space-y-6">
      {/* Controles */}
      <div className={`flex flex-wrap gap-4 items-center p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div>
          <label className="block text-xs font-medium mb-1">Format papier</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as FormatPapier)}
            className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
          >
            <option value="A4-paysage">A4 Paysage</option>
            <option value="A4-portrait">A4 Portrait</option>
            <option value="A3-paysage">A3 Paysage</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1">Emploi du temps</label>
          <select
            value={selectedIndex ?? 'tous'}
            onChange={(e) => setSelectedIndex(e.target.value === 'tous' ? null : parseInt(e.target.value))}
            className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
          >
            <option value="tous">Tous les emplois du temps</option>
            {emplois.map((e, i) => (
              <option key={i} value={i}>
                {e.titre} — {e.sous_titre}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 cursor-pointer self-end pb-1">
          <input
            type="checkbox"
            checked={showEnseignants}
            onChange={(e) => setShowEnseignants(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <span className="text-sm">Afficher les formateurs</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer self-end pb-1">
          <input
            type="checkbox"
            checked={hideInactif}
            onChange={(e) => setHideInactif(e.target.checked)}
            className="w-4 h-4 text-purple-600 rounded"
          />
          <span className="text-sm">Masquer matières inactives</span>
        </label>

        <div>
          <label className="block text-xs font-medium mb-1">Police</label>
          <select
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
            className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
          >
            {FONT_OPTIONS.map((f) => (
              <option key={f.label} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1">Taille ({fontScale}%)</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={70}
              max={150}
              step={5}
              value={fontScale}
              onChange={(e) => setFontScale(Number(e.target.value))}
              className="w-24"
            />
            <button
              onClick={() => setFontScale(100)}
              className={`text-xs px-2 py-1 rounded border ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2 self-end">
          <button
            onClick={() => handleExport('pdf')}
            disabled={emplois.length === 0 || exportingType !== null}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
          >
            {exportingType === 'pdf' ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
            PDF {selectedIndex === null && emplois.length > 1 ? `(${emplois.length})` : ''}
          </button>
          <button
            onClick={() => handleExport('word')}
            disabled={emplois.length === 0 || exportingType !== null}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
          >
            {exportingType === 'word' ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            Word {selectedIndex === null && emplois.length > 1 ? `(${emplois.length})` : ''}
          </button>
          <button
            onClick={handlePrint}
            disabled={emplois.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            <Printer size={16} />
            Imprimer
          </button>
        </div>
      </div>

      {/* Apercu */}
      {emplois.length === 0 ? (
        <div className={`text-center py-16 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <p className="text-gray-500">Aucun emploi du temps genere. Lancez d'abord la generation.</p>
        </div>
      ) : (
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-4">
            <Eye size={18} className="text-gray-400" />
            <span className="text-sm text-gray-500">Apercu — {emploisToShow.length} page(s)</span>
          </div>

          <div ref={printRef}>
            {emploisToShow.map((emploi, idx) => (
              <EmploiTempsPage
                key={idx}
                data={emploi}
                showEnseignants={showEnseignants}
                hideInactif={hideInactif}
                pageNum={idx + 1}
                totalPages={emploisToShow.length}
                isLast={idx === emploisToShow.length - 1}
                fontFamily={fontFamily}
                fontScale={fontScale}
                institutVille={institut?.ville}
                chefScolarite={institut?.chef_scolarite}
                institutTelephone={institut?.telephone}
                institutEmail={institut?.email}
                institutAdresseRue={institut?.adresse_rue}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface PageProps {
  data: EmploiTempsComplet;
  showEnseignants: boolean;
  hideInactif: boolean;
  pageNum: number;
  totalPages: number;
  isLast: boolean;
  fontFamily: string;
  fontScale: number;
  institutVille?: string;
  chefScolarite?: string;
  institutTelephone?: string;
  institutEmail?: string;
  institutAdresseRue?: string;
}

const EmploiTempsPage: React.FC<PageProps> = ({ data, showEnseignants, hideInactif, pageNum, totalPages, isLast, fontFamily, fontScale, institutVille, chefScolarite, institutTelephone, institutEmail, institutAdresseRue }) => {
  const sp = (basePt: number) => `${(basePt * fontScale / 100).toFixed(1)}pt`;

  // Couleurs charte 2I : bleu marine + or/jaune
  const NAVY = '#1A2E4A';
  const NAVY_LIGHT = '#1F4788';
  const GOLD = '#FFD700';
  const GOLD_DARK = '#D4A017';

  // Alternance simple 2 lignes : blanc / bleu très clair.
  // Choix validé user 2026-08-15 — retour à un rendu sobre, texte foncé
  // très lisible sur les 2 fonds. Colonne date légèrement plus soutenue
  // pour rester identifiable en tête de jour.
  const JOUR_COLORS: { bg: string; date: string }[] = [
    { bg: '#FFFFFF', date: '#E8EEF5' }, // blanc / bleu ardoise clair pour la date
    { bg: '#F1F5FA', date: '#D9E2ED' }, // bleu très clair uniforme / bleu ardoise
  ];
  const jourColor = (idx: number) => JOUR_COLORS[idx % JOUR_COLORS.length];

  return (
    <div className={`emploi-page ${!isLast ? 'page-break' : ''}`} style={{ marginBottom: isLast ? 0 : '20px', fontFamily, color: '#1a1a1a', background: '#fff' }}>

      {/* === EN-TETE 3 COLONNES === */}
      <table className="header-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
        <tbody><tr>
          {/* Gauche : Ministere + Institut */}
          <td className="header-left" style={{ width: '45%', verticalAlign: 'top', paddingRight: '10px' }}>
            <div className="ministere" style={{ fontWeight: 700, fontSize: sp(7.5), textTransform: 'uppercase', lineHeight: 1.3, color: NAVY }}>
              MINISTERE DE L'ENSEIGNEMENT SUPERIEUR DE LA RECHERCHE SCIENTIFIQUE ET DE L'INNOVATION TECHNOLOGIQUE
            </div>
            <div className="institut-badge" style={{
              background: `linear-gradient(135deg, ${GOLD}, #FFA500)`,
              fontWeight: 700, fontSize: sp(9), padding: '4px 10px',
              margin: '5px 0', display: 'inline-block', borderRadius: '3px',
              color: NAVY, border: `1px solid ${GOLD_DARK}`,
            }}>
              INSTITUT INTERNATIONAL POLYTECHNIQUE &amp; COMMERCE 2I
            </div>
            <div className="contact" style={{ fontSize: sp(7), lineHeight: 1.5, color: '#333' }}>
              <div>Tel. : <strong>{institutTelephone || '+242 06 736 10 00'}</strong></div>
              <div>E-mail : {institutEmail || 'direction2i@institut-international-2i.com'}</div>
              <div>Adresse : {institutAdresseRue || '06, RUE LI-LELEMB BASE INDUSTRIELLE'} — {(institutVille || 'Pointe-Noire').toUpperCase()}</div>
              <div style={{ fontSize: sp(6.5), marginTop: '2px', fontStyle: 'italic' }}>Agree definitivement par l'Etat S/N 196 du 21/03/2021</div>
            </div>
          </td>

          {/* Centre : Titre examen */}
          <td className="header-center" style={{ width: '30%', textAlign: 'center', verticalAlign: 'middle' }}>
            <div className="exam-title" style={{
              background: `linear-gradient(135deg, ${NAVY}, ${NAVY_LIGHT})`,
              color: '#fff', fontWeight: 700, fontSize: sp(11),
              padding: '8px 14px', borderRadius: '5px', textTransform: 'uppercase',
              display: 'inline-block', marginBottom: '8px', letterSpacing: '1px',
              border: `2px solid ${GOLD}`,
            }}>
              EXAMEN PARTIEL
            </div>
            <br />
            <div className="session-badge" style={{
              background: `linear-gradient(135deg, ${GOLD}, #FFC000)`, fontWeight: 700, fontSize: sp(9),
              padding: '3px 14px', display: 'inline-block', borderRadius: '3px',
              color: NAVY, border: `1px solid ${GOLD_DARK}`,
            }}>
              {data.type_session}
            </div>
            <div style={{ fontSize: sp(8.5), marginTop: '5px', fontWeight: 600, color: NAVY }}>
              {data.semestre} — A.A. {data.annee_academique}
            </div>
          </td>

          {/* Droite : Republique + Logo */}
          <td className="header-right" style={{ width: '25%', textAlign: 'center', verticalAlign: 'top' }}>
            <div className="republique" style={{ fontWeight: 700, fontSize: sp(7.5), color: NAVY }}>
              REPUBLIQUE DU CONGO
            </div>
            <div className="devise" style={{ fontSize: sp(6.5), fontStyle: 'italic', marginBottom: '6px', color: '#555' }}>
              Unite — Travail — Progres
            </div>
            <img src="/logo.png" alt="Logo 2I" style={{ width: '75px', height: '75px', objectFit: 'contain' }} />
          </td>
        </tr></tbody>
      </table>

      {/* Separateur double */}
      <div style={{ borderTop: `3px solid ${NAVY}`, borderBottom: `1px solid ${GOLD}`, margin: '4px 0 8px 0', height: '4px' }} />

      {/* === BARRE SOUS-TITRE === */}
      <div className="subtitle-bar" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: `linear-gradient(135deg, ${NAVY}, ${NAVY_LIGHT})`,
        border: `2px solid ${GOLD}`, borderRadius: '5px',
        padding: '6px 14px', marginBottom: '10px',
      }}>
        <span className="niveau" style={{ fontWeight: 700, fontSize: sp(10), color: GOLD }}>
          {data.titre}
        </span>
        <span className="pole" style={{ fontWeight: 700, fontSize: sp(11), textTransform: 'uppercase', color: '#fff', letterSpacing: '0.5px' }}>
          {data.sous_titre}
        </span>
        <span className="info" style={{ fontSize: sp(8), color: '#ccc' }}>
          PERIODE: {data.periode}
        </span>
      </div>

      {/* === TABLEAU === */}
      <table className="schedule-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: sp(8.5), border: `2px solid ${NAVY}` }}>
        <thead>
          <tr>
            <th style={{
              background: `linear-gradient(180deg, ${NAVY}, ${NAVY_LIGHT})`,
              color: GOLD, fontWeight: 700, fontSize: sp(8.5),
              padding: '6px 8px', border: `1px solid ${NAVY}`,
              width: '80px', textTransform: 'uppercase',
            }}>
              DATES
            </th>
            <th style={{
              background: `linear-gradient(180deg, ${NAVY}, ${NAVY_LIGHT})`,
              color: GOLD, fontWeight: 700, fontSize: sp(8.5),
              padding: '6px 8px', border: `1px solid ${NAVY}`,
              width: '65px', textTransform: 'uppercase',
            }}>
              HORAIRE
            </th>
            {data.groupes.map((groupe) => (
              <th
                key={groupe.id}
                style={{
                  background: `linear-gradient(180deg, ${NAVY}, ${NAVY_LIGHT})`,
                  color: '#fff', fontWeight: 700, fontSize: sp(8.5),
                  padding: '6px 8px', border: `1px solid ${NAVY}`,
                  textTransform: 'uppercase', letterSpacing: '0.3px',
                }}
              >
                {groupe.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.jours.map((jour, jourIdx) => {
            const jc = jourColor(jourIdx);
            return jour.creneaux.map((creneau, creneauIdx) => (
              <tr key={`${jour.date}-${creneau.heure_label}`}>
                {creneauIdx === 0 && (
                  <td
                    className="date-cell"
                    rowSpan={jour.creneaux.length}
                    style={{
                      fontWeight: 700,
                      background: jc.date,
                      width: '80px',
                      borderRight: `2px solid ${GOLD_DARK}`,
                      border: '1px solid #b0b0b0',
                      padding: '5px 6px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                    }}
                  >
                    <div className="day-name" style={{ fontSize: sp(7.5), color: '#1a1a1a', fontWeight: 700 }}>
                      {jour.jour_semaine}
                    </div>
                    <div className="day-date" style={{ fontSize: sp(7), color: '#444' }}>
                      {jour.date_label}
                    </div>
                  </td>
                )}
                <td className="heure-cell" style={{
                  fontWeight: 700, width: '65px',
                  background: jc.date, color: '#1a1a1a',
                  fontSize: sp(7.5), border: '1px solid #b0b0b0',
                  padding: '5px 6px', textAlign: 'center',
                }}>
                  {creneau.heure_label}
                </td>
                {data.groupes.map((groupe) => {
                  const mat = creneau.matieres[groupe.id];
                  return (
                    <td
                      key={groupe.id}
                      className="matiere-cell"
                      style={{
                        border: '1px solid #b0b0b0',
                        padding: '5px 6px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        minHeight: '30px',
                        fontSize: sp(8),
                        fontWeight: mat ? 600 : 'normal',
                        backgroundColor: jc.bg,
                        // Texte noir profond pour un contraste maximum
                        // sur les fonds clairs (blanc + bleu très clair)
                        color: mat ? '#1a1a1a' : '#ccc',
                      }}
                    >
                      {mat ? (
                        hideInactif && mat.est_inactif ? null : (
                          <>
                            <span style={mat.est_inactif ? { textDecoration: 'line-through', color: '#999' } : { color: '#1a1a1a' }}>
                              {mat.matiere_nom}
                            </span>
                            {mat.duree_label && !mat.est_inactif && (
                              <div style={{ fontSize: sp(6), color: '#D97706', fontWeight: 700, marginTop: '1px' }}>
                                ({mat.duree_label})
                              </div>
                            )}
                            {showEnseignants && mat.enseignant_noms.length > 0 && !mat.est_inactif && (
                              <div className="enseignant-nom" style={{ fontSize: sp(6), fontStyle: 'italic', color: '#777', marginTop: '2px' }}>
                                {Array.from(new Set(mat.enseignant_noms)).join(', ')}
                              </div>
                            )}
                          </>
                        )
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ));
          })}
        </tbody>
      </table>

      {/* === PIED DE PAGE === */}
      <div className="footer-section" style={{ marginTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div className="footer-left" style={{ fontSize: sp(6.5), color: '#888', borderTop: `1px solid ${GOLD}`, paddingTop: '5px' }}>
          EXAMEN PARTIEL — {data.type_session} — {data.sous_titre} — {data.titre.replace('NIVEAU: ', '')}
          <span style={{ marginLeft: '15px' }}>Page {pageNum}/{totalPages}</span>
        </div>
        <div className="footer-right" style={{ textAlign: 'right', fontSize: sp(7.5) }}>
          <div className="lieu">Fait a {institutVille || 'Pointe-Noire'} le....................................</div>
          <div className="service" style={{ fontStyle: 'italic', marginBottom: '2px', color: '#444' }}>
            Le Service de la Scolarite et des Examens.
          </div>
          <div className="signature" style={{ fontWeight: 700, marginTop: '20px', color: NAVY }}>
            {chefScolarite || 'Donald Christ MBEMBA LOKO'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmploiTempsImpression;
